import {cva} from 'platform/shared';

import {
  AriaRole,
  CSSProperties,
  FocusEventHandler,
  KeyboardEventHandler,
  MouseEvent,
  MouseEventHandler,
  ReactNode,
} from 'react';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {useResponsivePropValue} from '../../hooks/useResponsivePropValue';
import {CSSDimension} from '../../types/CSSDimension';
import {Integer} from '../../types/Integer';
import {ValueByDevice} from '../../types/ValueByDevice';
import {getCssSize} from '../../utils/getCssSize';

const clickableVariants = cva(
  'focus-visible:[outline:var(--outline-default)_var(--color-general-accent)] focus-visible:[outline-offset:var(--outline-offset)]',
  {
    variants: {
      isClickable: {
        true: 'cursor-pointer',
        false: 'cursor-[initial]',
      },
      isDisabled: {
        true: 'pointer-events-none',
        false: '[pointer-events:all]',
      },
      isTextSelectable: {
        true: 'select-text',
        false: 'select-none',
      },
    },
  }
);

export interface ClickableProps extends TestIdProps {
  children?: ReactNode;
  width?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer>;
  height?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer>;
  maxHeight?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer>;
  maxWidth?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer>;
  minHeight?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer>;
  minWidth?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer>;
  isDisabled?: boolean | Nullish;
  /**
   * @about Keeps the text inside selectable, so it can be copied.
   */
  isTextSelectable?: boolean;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  onMouseWheelClick?: MouseEventHandler<HTMLDivElement>;
  onContextMenu?: MouseEventHandler<HTMLDivElement>;
  onMouseEnter?: MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: MouseEventHandler<HTMLDivElement>;
  onFocus?: FocusEventHandler<HTMLDivElement>;
  onBlur?: FocusEventHandler<HTMLDivElement>;
  role?: AriaRole;
  tabIndex?: number;
  'aria-pressed'?: boolean;
  'aria-label'?: string;
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
}

export function Clickable(props: ClickableProps) {
  const width = useResponsivePropValue(props.width);
  const height = useResponsivePropValue(props.height);
  const maxWidth = useResponsivePropValue(props.maxWidth);
  const maxHeight = useResponsivePropValue(props.maxHeight);
  const minWidth = useResponsivePropValue(props.minWidth);
  const minHeight = useResponsivePropValue(props.minHeight);

  const onAuxClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.button === 1) {
      props.onMouseWheelClick?.(event);
    }
  };

  const style: CSSProperties = {
    height: getCssSize(height),
    width: getCssSize(width),
    maxHeight: getCssSize(maxHeight),
    minHeight: getCssSize(minHeight),
    maxWidth: getCssSize(maxWidth),
    minWidth: getCssSize(minWidth),
  };

  return (
    <div
      role={props.role}
      tabIndex={props.isDisabled ? undefined : props.tabIndex}
      aria-pressed={props['aria-pressed']}
      aria-label={props['aria-label']}
      aria-disabled={props.isDisabled || undefined}
      aria-expanded={props['aria-expanded']}
      aria-controls={props['aria-controls']}
      onClick={props.onClick}
      onKeyDown={props.onKeyDown}
      onAuxClick={onAuxClick}
      onContextMenu={props.onContextMenu}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
      onFocus={props.onFocus}
      onBlur={props.onBlur}
      data-testid={suffixTestId('clickable', props)}
      className={clickableVariants({
        isClickable: !(props.isDisabled ?? !props.onClick),
        isDisabled: Boolean(props.isDisabled),
        isTextSelectable: Boolean(props.isTextSelectable),
      })}
      style={style}
    >
      {props.children}
    </div>
  );
}
